/**
 * "Markdown leve": `**negrito**`, `*itálico*`, `` `código` ``, parágrafos e listas com
 * hífen. Nada mais — título, link e tabela saem como texto.
 *
 * ⚠️ assumido — §9: que o agente responde SÓ com isso é suposição. O prompt do
 * coordenador não fixa formato, e nenhuma resposta real foi lida até 05/10/2026. Se
 * vierem `###` ou tabelas, aparecem crus; a correção é uma linha de formato no
 * prompt do backend, ou estender este arquivo.
 *
 * 🔒 Constrói ELEMENTOS, nunca HTML: não há `dangerouslySetInnerHTML` aqui, então
 * uma resposta com `<script>` é só uma frase estranha na tela.
 *
 * Sem `react-markdown` de propósito: são ~40 kB gz para três construções, num
 * painel que existe em todas as telas.
 */
import { Fragment, type ReactNode } from 'react'

const ITEM_DE_LISTA = /^\s*[-*]\s+/

interface Bloco {
  tipo: 'paragrafo' | 'lista'
  linhas: string[]
}

function emBlocos(texto: string): Bloco[] {
  const blocos: Bloco[] = []
  let atual: Bloco | null = null

  for (const linha of texto.replace(/\r\n/g, '\n').split('\n')) {
    if (linha.trim() === '') {
      atual = null
      continue
    }
    const tipo: Bloco['tipo'] = ITEM_DE_LISTA.test(linha) ? 'lista' : 'paragrafo'
    if (!atual || atual.tipo !== tipo) {
      atual = { tipo, linhas: [] }
      blocos.push(atual)
    }
    atual.linhas.push(tipo === 'lista' ? linha.replace(ITEM_DE_LISTA, '') : linha)
  }
  return blocos
}

/** `` `x` `` — o conteúdo do código não passa por nenhuma outra regra. */
const CODIGO = /(`[^`\n]+`)/g
const NEGRITO = /(\*\*[^*]+\*\*)/g
/**
 * `*x*` — só quando o `*` não encosta em outro `*` (senão `**x*y**` viraria
 * itálico) nem em espaço do lado de dentro (senão `2 * 3 * 4` viraria itálico),
 * como no CommonMark.
 */
const ITALICO = /((?<![*\w])\*(?![\s*])[^*\n]+?(?<![\s*])\*(?![*\w]))/g

/**
 * Código em linha, depois `**negrito**`, depois `*itálico*`. Delimitador sem par não
 * casa e fica como veio.
 *
 * O `split` com grupo de captura devolve os trechos casados nas posições ÍMPARES —
 * é por elas que se decide, e não olhando se o texto "parece" formatado: `**x*y**`
 * começa e termina com `**`, mas não casa, e tem que sair literal.
 *
 * Itálico e código entraram em 14/10/2026: a resposta real do Especialista FinOps
 * trouxe `*proposta*` e `` `db-orgao-b-prod` ``, e o painel mostrava os símbolos.
 */
function emLinha(texto: string): ReactNode[] {
  return texto.split(CODIGO).map((parte, indice) => {
    if (indice % 2 === 1) return <code key={indice}>{parte.slice(1, -1)}</code>
    return parte === '' ? null : <Fragment key={indice}>{negritos(parte)}</Fragment>
  })
}

function negritos(texto: string): ReactNode[] {
  return texto.split(NEGRITO).map((parte, indice) => {
    if (indice % 2 === 1) return <strong key={indice}>{italicos(parte.slice(2, -2))}</strong>
    return parte === '' ? null : <Fragment key={indice}>{italicos(parte)}</Fragment>
  })
}

function italicos(texto: string): ReactNode[] {
  return texto.split(ITALICO).map((parte, indice) => {
    if (indice % 2 === 1) return <em key={indice}>{parte.slice(1, -1)}</em>
    return parte === '' ? null : <Fragment key={indice}>{parte}</Fragment>
  })
}

export function MarkdownLeve({ texto }: { texto: string }) {
  return emBlocos(texto).map((bloco, indice) =>
    bloco.tipo === 'lista' ? (
      <ul key={indice} className="list-disc pl-5">
        {bloco.linhas.map((linha, i) => (
          <li key={i}>{emLinha(linha)}</li>
        ))}
      </ul>
    ) : (
      <p key={indice}>
        {bloco.linhas.map((linha, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {emLinha(linha)}
          </Fragment>
        ))}
      </p>
    ),
  )
}
